import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  Button,
  CircularProgress,
  useTheme
} from '@mui/material';
import { PlayArrow as PlayArrowIcon } from '@mui/icons-material';
import { useSnackbar } from '../../../context/SnackbarContext';
import {
  useCampaignId,
  useSpreadsheetViewType
} from '../../../context/CampaignContext';
import type { SpreadsheetRefHandle } from '../../spreadsheet/spreadsheetTypes';
import ProspectClassificationPanelHeader from './ProspectClassificationPanelHeader';
import TieringSection from './TieringSection';
import type {
  ProspectClassificationConfig,
  ClassificationMethod,
  TierLabel
} from './types';
import { defaultClassificationConfig } from './types';
import {
  getCampaignPrioritization,
  getPrioritizationOutputColumnNames,
  upsertCampaignPrioritization,
  PrioritizationType,
  type CampaignPrioritizationRequest
} from '../../../api/campaignPrioritization';
import { getCells } from '../../../api/spreadsheet';
import { PrioritizationAbortNoProspectsError } from '../../spreadsheet/spreadsheetPrioritization';

interface ProspectClassificationToolPanelProps {
  onClose: () => void;
  /** Spreadsheet ref for starting prioritization and applying results. */
  spreadsheetRef: React.RefObject<SpreadsheetRefHandle | null>;
  /** True while prioritization workflow or pre-stream lock is active on the sheet (not enrichment-only). */
  isProspectPrioritizationActive: boolean;
  /** Row indices currently hidden by the sheet filter; prioritization will only run on visible rows. */
  getFilteredOutRowIds?: () => number[] | null;
  /** When true, enrichment or prioritization locks the spreadsheet; prioritization cannot be started. */
  spreadsheetJobLocked?: boolean;
  /** When true, enrichment (not prioritization) locks the sheet — enrichment-specific banner/snackbar only. */
  spreadsheetLockedDuringEnrichment?: boolean;
}

/** Maps API response to ProspectClassificationConfig. */
function apiResponseToConfig(
  prioritization: {
    prioritization_type: string;
    config: Record<string, unknown>;
  } | null
): ProspectClassificationConfig {
  if (!prioritization) return defaultClassificationConfig;

  const type = prioritization.prioritization_type;
  const config = prioritization.config;

  if (
    type === PrioritizationType.TIERING &&
    config.tiers &&
    Array.isArray(config.tiers)
  ) {
    const tierLabels: TierLabel[] = (
      config.tiers as Array<{ label?: string; criteria?: string }>
    ).map((t, i) => ({
      id: `tier-${i}-${t.label ?? ''}`,
      label: t.label ?? '',
      criteria: t.criteria ?? ''
    }));
    return {
      ...defaultClassificationConfig,
      classificationMethod: PrioritizationType.TIERING,
      tierLabels
    };
  }

  if (type === PrioritizationType.QUALIFYING) {
    return {
      ...defaultClassificationConfig,
      classificationMethod: PrioritizationType.QUALIFYING,
      qualifiedCriteria: (config.qualified_criteria as string) ?? '',
      unqualifiedCriteria: (config.unqualified_criteria as string) ?? ''
    };
  }

  if (type === PrioritizationType.SCORING) {
    return {
      ...defaultClassificationConfig,
      classificationMethod: PrioritizationType.SCORING,
      scoreInstructions: (config.criteria as string) ?? ''
    };
  }

  return defaultClassificationConfig;
}

/** Deep equality for configs - compares tierLabels by label/criteria only (id is ephemeral). */
function configsEqual(
  a: ProspectClassificationConfig,
  b: ProspectClassificationConfig
): boolean {
  if (
    a.classificationMethod !== b.classificationMethod ||
    a.scoreInstructions !== b.scoreInstructions ||
    a.qualifiedCriteria !== b.qualifiedCriteria ||
    a.unqualifiedCriteria !== b.unqualifiedCriteria
  ) {
    return false;
  }
  const tiersA = a.tierLabels.map((t) => ({
    label: t.label,
    criteria: t.criteria
  }));
  const tiersB = b.tierLabels.map((t) => ({
    label: t.label,
    criteria: t.criteria
  }));
  if (tiersA.length !== tiersB.length) return false;
  return tiersA.every(
    (t, i) => t.label === tiersB[i].label && t.criteria === tiersB[i].criteria
  );
}

/** Maps ProspectClassificationConfig to API request payload. */
function configToRequest(
  c: ProspectClassificationConfig
): CampaignPrioritizationRequest {
  if (c.classificationMethod === PrioritizationType.TIERING) {
    const tiers = c.tierLabels
      .filter((t) => t.label.trim() || t.criteria.trim())
      .map(({ label, criteria }) => ({ label, criteria }));
    if (tiers.length === 0) {
      tiers.push({ label: 'Tier 1', criteria: '' });
    }
    return {
      prioritization_type: PrioritizationType.TIERING,
      config: { tiers }
    };
  }
  if (c.classificationMethod === PrioritizationType.QUALIFYING) {
    return {
      prioritization_type: PrioritizationType.QUALIFYING,
      config: {
        qualified_criteria: c.qualifiedCriteria,
        unqualified_criteria: c.unqualifiedCriteria
      }
    };
  }
  return {
    prioritization_type: PrioritizationType.SCORING,
    config: { criteria: c.scoreInstructions }
  };
}

/** Returns true if config has valid criteria for the selected method. */
function isConfigValid(c: ProspectClassificationConfig): boolean {
  if (c.classificationMethod === PrioritizationType.SCORING) {
    return c.scoreInstructions.trim().length > 0;
  }
  if (c.classificationMethod === PrioritizationType.TIERING) {
    const valid = c.tierLabels.filter(
      (t) => t.label.trim() && t.criteria.trim()
    );
    return valid.length > 0;
  }
  if (c.classificationMethod === PrioritizationType.QUALIFYING) {
    return (
      c.qualifiedCriteria.trim().length > 0 ||
      c.unqualifiedCriteria.trim().length > 0
    );
  }
  return false;
}

const ProspectClassificationToolPanel: React.FC<
  ProspectClassificationToolPanelProps
> = ({
  onClose,
  spreadsheetRef,
  isProspectPrioritizationActive,
  getFilteredOutRowIds,
  spreadsheetJobLocked,
  spreadsheetLockedDuringEnrichment = false
}) => {
  const campaignId = useCampaignId();
  const viewType = useSpreadsheetViewType();
  const { showSnackbar } = useSnackbar();
  const theme = useTheme();

  const [config, setConfig] = useState<ProspectClassificationConfig>(
    defaultClassificationConfig
  );
  const [initialConfig, setInitialConfig] =
    useState<ProspectClassificationConfig>(defaultClassificationConfig);
  const [isEditMode, setIsEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!campaignId) {
      setConfig(defaultClassificationConfig);
      setInitialConfig(defaultClassificationConfig);
      setIsEditMode(false);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getCampaignPrioritization(campaignId, viewType)
      .then(({ prioritization }) => {
        if (!cancelled) {
          const loaded = apiResponseToConfig(prioritization);
          setConfig(loaded);
          setInitialConfig(loaded);
          // View mode when we have saved config; edit when empty
          const hasConfig = prioritization !== null;
          setIsEditMode(!hasConfig);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          showSnackbar(
            `Failed to load prioritization: ${err instanceof Error ? err.message : String(err)}`,
            'error'
          );
          const fallback = defaultClassificationConfig;
          setConfig(fallback);
          setInitialConfig(fallback);
          setIsEditMode(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [campaignId, viewType, showSnackbar]);

  const setClassificationMethod = useCallback(
    (method: ClassificationMethod) => {
      setConfig((prev) => ({
        ...prev,
        classificationMethod: method
      }));
    },
    []
  );

  const setScoreInstructions = useCallback((value: string) => {
    setConfig((prev) => ({ ...prev, scoreInstructions: value }));
  }, []);

  const setTierLabels = useCallback((labels: TierLabel[]) => {
    setConfig((prev) => ({ ...prev, tierLabels: labels }));
  }, []);

  const setQualifiedCriteria = useCallback((value: string) => {
    setConfig((prev) => ({ ...prev, qualifiedCriteria: value }));
  }, []);

  const setUnqualifiedCriteria = useCallback((value: string) => {
    setConfig((prev) => ({ ...prev, unqualifiedCriteria: value }));
  }, []);

  const handleSave = async () => {
    if (!campaignId) {
      showSnackbar('No campaign selected', 'error');
      return;
    }

    if (
      config.classificationMethod === PrioritizationType.SCORING &&
      !config.scoreInstructions.trim()
    ) {
      showSnackbar(
        'Please describe how you want to rank your company prospects',
        'warning'
      );
      return;
    }

    if (config.classificationMethod === PrioritizationType.TIERING) {
      const invalidLabels = config.tierLabels.filter(
        (l) => !l.label.trim() || !l.criteria.trim()
      );
      if (config.tierLabels.length === 0) {
        showSnackbar(
          'Add at least one tier with label and criteria for company prospects',
          'warning'
        );
        return;
      }
      if (invalidLabels.length > 0) {
        showSnackbar(
          'Fill in label name and criteria for all tiers',
          'warning'
        );
        return;
      }
    }

    if (config.classificationMethod === PrioritizationType.QUALIFYING) {
      if (
        !config.qualifiedCriteria.trim() &&
        !config.unqualifiedCriteria.trim()
      ) {
        showSnackbar(
          'Specify qualified and/or unqualified criteria for company prospects',
          'warning'
        );
        return;
      }
    }

    setSaving(true);
    try {
      await upsertCampaignPrioritization(
        campaignId,
        configToRequest(config),
        viewType
      );
      setInitialConfig(config);
      setIsEditMode(false);
      showSnackbar('Company prospect prioritization saved', 'success');
    } catch (err) {
      showSnackbar(
        `Failed to save: ${err instanceof Error ? err.message : String(err)}`,
        'error'
      );
    } finally {
      setSaving(false);
    }
  };

  const hasChanged = !configsEqual(config, initialConfig);
  const canStart =
    !hasChanged &&
    isConfigValid(config) &&
    !!campaignId &&
    !saving &&
    !isProspectPrioritizationActive &&
    !spreadsheetJobLocked;

  const handleStartPrioritization = async () => {
    if (spreadsheetJobLocked) {
      showSnackbar(
        spreadsheetLockedDuringEnrichment
          ? 'Spreadsheet is locked during enrichment. Please wait for it to complete.'
          : 'Spreadsheet is locked during prioritization. Please wait for it to complete.',
        'info'
      );
      return;
    }
    if (!campaignId) {
      showSnackbar('No campaign selected', 'error');
      return;
    }
    if (hasChanged) {
      showSnackbar(
        'Save your configuration first, then run prioritization',
        'warning'
      );
      return;
    }
    if (!isConfigValid(config)) {
      showSnackbar(
        'Complete the configuration before starting prioritization',
        'warning'
      );
      return;
    }

    const spreadsheet = spreadsheetRef.current;
    if (!spreadsheet) {
      showSnackbar(
        "Spreadsheet isn't ready yet. Please try again in a moment.",
        'warning'
      );
      return;
    }

    try {
      await spreadsheet.startProspectPrioritization(async () => {
        const { cells } = await getCells(campaignId, viewType);
        let rowIds = Array.from(new Set(cells.map((c) => c.row_id))).sort(
          (a, b) => a - b
        );
        const filteredOut = getFilteredOutRowIds?.();
        if (Array.isArray(filteredOut) && filteredOut.length > 0) {
          const hiddenSet = new Set(filteredOut);
          rowIds = rowIds.filter((id) => !hiddenSet.has(id));
        }
        if (rowIds.length === 0) {
          showSnackbar(
            'No prospects to prioritize. Add prospects to your campaign first.',
            'warning'
          );
          throw new PrioritizationAbortNoProspectsError();
        }
        return rowIds;
      }, getPrioritizationOutputColumnNames(config.classificationMethod));
    } catch {
      // `runProspectPrioritizationFromPanel` surfaces most failures with a snackbar internally.
      // Absorb escapes (e.g. snackbar throwing in that catch, or other unexpected rejects) so the click handler never rejects.
    }
  };

  const handleEdit = () => setIsEditMode(true);
  const handleCancel = () => {
    setConfig(initialConfig);
    setIsEditMode(false);
  };

  const hasTierLabels = config.tierLabels.length > 0;
  const tierLabelsWithDefault = hasTierLabels
    ? config.tierLabels
    : [{ id: 'default-empty', label: 'Tier 1', criteria: '' }];

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          position: 'relative'
        }}
      >
        <ProspectClassificationPanelHeader onClose={onClose} />
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2
          }}
        >
          <CircularProgress size={32} />
          <Typography variant="body2" color="text.secondary">
            Loading prioritization...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        position: 'relative'
      }}
    >
      <ProspectClassificationPanelHeader
        onClose={onClose}
        onEdit={handleEdit}
        showEditButton={!isEditMode && !isProspectPrioritizationActive}
      />

      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 3
        }}
      >
        {/* Classification method: 3 options */}
        <Box>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              color: theme.palette.text.primary,
              mb: 1.5
            }}
          >
            How do you want to prioritize your company prospects?
          </Typography>
          <ToggleButtonGroup
            value={config.classificationMethod}
            exclusive
            onChange={(_, value: ClassificationMethod | null) =>
              isEditMode && value && setClassificationMethod(value)
            }
            fullWidth
            disabled={!isEditMode || isProspectPrioritizationActive}
            sx={{
              '& .MuiToggleButton-root': {
                fontSize: '0.85rem',
                fontWeight: 500,
                textTransform: 'none',
                border: `1px solid ${theme.palette.divider}`,
                py: 1,
                '&.Mui-selected': {
                  backgroundColor:
                    theme.customColors?.themePrimary || 'primary.main',
                  color: theme.customColors?.themeSecondary || 'white',
                  borderColor:
                    theme.customColors?.themePrimary || 'primary.main',
                  '&:hover': {
                    backgroundColor:
                      theme.customColors?.themePrimary || 'primary.main',
                    opacity: 0.92
                  }
                }
              }
            }}
          >
            <ToggleButton value={PrioritizationType.TIERING}>
              Tiering
            </ToggleButton>
            <ToggleButton value={PrioritizationType.QUALIFYING}>
              Qualifying
            </ToggleButton>
            <ToggleButton value={PrioritizationType.SCORING}>
              Scoring
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Content section - instant switch, no slide animation */}
        {config.classificationMethod === PrioritizationType.SCORING && (
          <Box>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                color: theme.palette.text.secondary,
                mb: 1
              }}
            >
              Describe how you want us to rank your company prospects.
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={4}
              placeholder="e.g. Rank company prospects by job title (CEO, VP Sales higher). Prioritize companies with revenue above $10M. Lower score for prospects who haven't engaged in the last 30 days."
              value={config.scoreInstructions}
              onChange={(e) => setScoreInstructions(e.target.value)}
              disabled={!isEditMode || isProspectPrioritizationActive}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2
                }
              }}
            />
          </Box>
        )}

        {config.classificationMethod === PrioritizationType.TIERING && (
          <TieringSection
            labels={tierLabelsWithDefault}
            onLabelsChange={setTierLabels}
            disabled={!isEditMode || isProspectPrioritizationActive}
          />
        )}

        {config.classificationMethod === PrioritizationType.QUALIFYING && (
          <Box>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                color: theme.palette.text.secondary,
                mb: 2
              }}
            >
              Define what makes a company prospect qualified or unqualified.
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Qualified"
              placeholder="e.g. Company size > 50, in target industry, has valid contact email"
              value={config.qualifiedCriteria}
              onChange={(e) => setQualifiedCriteria(e.target.value)}
              disabled={!isEditMode || isProspectPrioritizationActive}
              sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Unqualified"
              placeholder="e.g. Competitor companies, invalid/bounced emails, already a customer, outside target geography"
              value={config.unqualifiedCriteria}
              onChange={(e) => setUnqualifiedCriteria(e.target.value)}
              disabled={!isEditMode || isProspectPrioritizationActive}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Box>
        )}

        {isEditMode && (
          <Box
            sx={{
              pt: 2,
              mt: 2,
              borderTop: '1px solid',
              borderColor: 'divider',
              flexShrink: 0,
              display: 'flex',
              gap: 1
            }}
          >
            <Button
              variant="outlined"
              fullWidth
              onClick={handleCancel}
              disabled={saving || isProspectPrioritizationActive}
              sx={(t) => ({
                borderRadius: '50px',
                textTransform: 'capitalize',
                py: 1.5,
                border: `1px solid ${t.customColors?.themePrimary || 'primary.main'}`,
                color: t.customColors?.themePrimary || 'primary.main',
                fontWeight: 600,
                fontSize: '0.95rem'
              })}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              fullWidth
              onClick={handleSave}
              disabled={saving || isProspectPrioritizationActive || !hasChanged}
              sx={{
                borderRadius: '50px',
                textTransform: 'capitalize',
                py: 1.5,
                backgroundColor:
                  theme.customColors?.themePrimary || 'primary.main',
                color: theme.customColors?.themeSecondary || 'white',
                fontWeight: 600,
                fontSize: '0.8125rem',
                '&:hover': {
                  backgroundColor:
                    theme.customColors?.themePrimary || 'primary.main',
                  opacity: 0.9,
                  transform: 'translateY(-2px)',
                  boxShadow: `0 6px 20px ${theme.customColors?.themePrimary || '#1976d2'}40`
                },
                transition: 'all 0.2s ease',
                '&.Mui-disabled': { opacity: 0.6 }
              }}
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </Box>
        )}
      </Box>

      {/* Start prioritization - at bottom in view mode, like CRM Sync */}
      {!isEditMode && (
        <Box
          sx={{
            mt: 'auto',
            pt: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            flexShrink: 0
          }}
        >
          {spreadsheetLockedDuringEnrichment && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Spreadsheet is locked during enrichment. Wait for it to complete
              before running prioritization.
            </Typography>
          )}
          <Button
            variant="contained"
            fullWidth
            onClick={handleStartPrioritization}
            disabled={!canStart}
            startIcon={
              isProspectPrioritizationActive ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <PlayArrowIcon sx={{ fontSize: 20 }} />
              )
            }
            sx={(t) => ({
              borderRadius: '50px',
              textTransform: 'capitalize',
              py: 1.5,
              fontWeight: 600,
              fontSize: '0.95rem',
              border: `1px solid ${t.customColors?.themePrimary || 'primary.main'}`,
              backgroundColor: t.customColors?.themePrimary || 'primary.main',
              color: t.customColors?.themeSecondary || 'white',
              '&:disabled': { borderColor: 'inherit', opacity: 0.6 }
            })}
          >
            {isProspectPrioritizationActive
              ? 'Prioritizing…'
              : 'Start prioritization'}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default ProspectClassificationToolPanel;
export type {
  ProspectClassificationConfig,
  ClassificationMethod,
  TierLabel
} from './types';
